import Autocomplete from "./library/Autocomplete";
import {IncludeExclude, PublisherOrSiteOption, PublisherRecord, PublisherSiteRecord} from "../utils/types";
import { checkForMatch } from "../utils/misc";
import { Form } from "react-bootstrap";
import './TrafficSourcesAutocomplete.css';
import { useState } from "react";
import { isAllowedFilterField } from "../utils/UserHelper";

/**
 * The text used when showing the selected options as labels that can be [X]ed out individually
 * does not influence the rendering of the menu, just of the selected items
 * 
 * Technically, option could just be a PublisherRecord, not a PublisherSiteRecord, but they have
 * mostly the same signature, except for the bits we check manually.
 */
export const TrafficSourceLabelKeyFunction = function (option: PublisherOrSiteOption) {
  const publisher:PublisherRecord = (option.record as PublisherRecord);
  const pubSite:PublisherSiteRecord = (option.record as PublisherSiteRecord);

  if ( pubSite && pubSite.apptapSiteId ){
    return pubSite.apptapPublisherName + " - " + pubSite.apptapPublisherSiteName + " (" + pubSite.apptapPublisherId + ":" + pubSite.apptapSiteId + ")";
  } 
  else {
    return publisher.apptapPublisherName + " (" + publisher.apptapPublisherId + ":<all sites>)";
  }
}

interface TrafficSourcesAutocompleteProps {
  onChange?: (newSelected:PublisherOrSiteOption[], isSelectedFromCheckbox?:boolean) => void;
  onInputChange?: (query: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onMenuToggle?: (isOpen:boolean) => void;
  reactRef: any;
  options: PublisherOrSiteOption[];
  selected?: PublisherOrSiteOption[];
  disabled?: boolean;
  multiple?: boolean;
  selectedForCheckboxes?: PublisherOrSiteOption[];
  usePublisherDisabled?: boolean;
  useCheckbox?: boolean;
  style?: {[key:string]:string};
  useIncludeExclude?: boolean;
  defaultIncludeExclude?: IncludeExclude;
  includeExcludeOnChange?: (newIncludeExclude:IncludeExclude) => void;
}

function TrafficSourcesAutocomplete(props: TrafficSourcesAutocompleteProps) {
  const [arrowMode, setArrowMode] = useState(false);

  const handleKeyDown = (e:React.KeyboardEvent) => {
    switch(e.code) {
      case "Enter":
        const activeIndex = props.reactRef.current.state.activeIndex;
        props.reactRef.current.toggleMenu();
        props.reactRef.current.setState({...props.reactRef.current.state, activeIndex: activeIndex});
        break;
      case "ArrowDown":
      case "ArrowUp":
        setArrowMode(true);
        break;
      case "Space":
        if (arrowMode) {
          e.preventDefault();
          handleCheckboxClick(e, props.reactRef.current.state.activeItem);
        }
        break;
      default:
        setArrowMode(false);
        break;
    }
  }

  const handleCheckboxClick = (e:any, selectedValue:PublisherOrSiteOption) => {
    e.stopPropagation();

    const selectedValues = props.selected ? [...props.selected, selectedValue] : [selectedValue];
    props.onChange(selectedValues, true);
  }

  const renderMenuItemChildren = function (option: (PublisherOrSiteOption) , obj : any, index : number) {
    const publisher:PublisherRecord = (option.record as PublisherRecord);
    const pubSite:PublisherSiteRecord = (option.record as PublisherSiteRecord);

    const isChecked = props.selectedForCheckboxes.find(selected => selected.id === option.id) != null;
    const checkboxElement = 
      props.useCheckbox && !option.disabled ?
        <Form.Check
          key={option.id + '-' + isChecked.toString()}
          inline
          readOnly
          style={{ whiteSpace: "nowrap", margin: "0 5px", cursor: "pointer" }}
          type="checkbox"
          disabled={option.disabled}
          checked={isChecked}
          onClick={(e:any) => handleCheckboxClick(e, option)}
        />
      : null;

    const pubSiteId = pubSite.apptapSiteId ? pubSite.apptapPublisherId + ":" + pubSite.apptapSiteId : publisher.apptapPublisherId;
    const name = pubSite.apptapSiteId ? pubSite.apptapPublisherSiteName : publisher.apptapPublisherName;
    const menuItem = (
      <div style={{ padding: pubSite.apptapSiteId && !props.usePublisherDisabled ? "0.25rem 1.5rem 0.25rem 3.2rem" : "0.25rem 1.5rem", display: "flex", justifyContent: "space-between" }}>
        <strong style={{ display: "flex", marginLeft: !checkboxElement && !option.disabled ? "20px" : null}}>{checkboxElement}{name}</strong>
        <span style={{float:"right", color: "#717171"}}>{pubSiteId}</span>
      </div>
    )

    return menuItem;
  }

  const filterPublisherSites = function(option: PublisherOrSiteOption, props: any) {
    if ( props.text == null ) return true;
    const searchString = (props.text as string).trim().toLowerCase();
    if ( props.text.length === 0 ) return true;

    let searchStrings:string[] = searchString.split(" ");

    // this is pre-processed in App.tsx
    // and already lower-cased and a mega-text containing:
    //   publisher ID, publisher name,
    //   all site IDs, all site names
    return checkForMatch(searchStrings, option.text);
  }

  return <Autocomplete 
    id="trafficSourcesAutocomplete1"
    renderMenuItemChildren={renderMenuItemChildren}  
    placeholder="Publishers and Sites" 
    reactKey="trafficSourcesAutocomplete" 
    labelKey={TrafficSourceLabelKeyFunction} 
    onChange={props.onChange}
    onKeyDown={(e:React.KeyboardEvent) => handleKeyDown(e)}
    options={props.options} 
    selected={props.selected}
    filterBy={filterPublisherSites} 
    reactRef={props.reactRef}
    disabled={props.disabled}
    multiple={props.multiple}
    style={props.style}
    onInputChange={props.onInputChange}
    onMenuToggle={props.onMenuToggle}
    useIncludeExclude={props.useIncludeExclude && isAllowedFilterField("placementsExclude")}
    defaultIncludeExclude={props.defaultIncludeExclude}
    includeExcludeOnChange={props.includeExcludeOnChange}
    />;
}

export default TrafficSourcesAutocomplete;